package com.yet.tetris.database.dao

import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOne
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull
import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToOneOrNull
import com.app.common.AppDispatchers
import com.yet.tetris.database.BoardCells
import com.yet.tetris.database.CurrentGameState
import com.yet.tetris.database.db.DatabaseManager
import com.yet.tetris.domain.model.game.TetrominoType
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.withContext

class GameStateDao(
    private val databaseManager: DatabaseManager,
    private val dispatchers: AppDispatchers,
) {
    suspend fun saveGameState(
        score: Long,
        linesCleared: Long,
        level: Long,
        piecesPlaced: Long = 0,
        maxCombo: Long = 0,
        tetrisesCleared: Long = 0,
        tSpinClears: Long = 0,
        perfectClears: Long = 0,
        hardDrops: Long = 0,
        hardDropCells: Long = 0,
        softDropCells: Long = 0,
        backToBackChain: Long = 0,
        isTSpinEligible: Boolean = false,
        currentPieceType: TetrominoType?,
        currentPieceRotation: Long,
        currentPositionX: Long,
        currentPositionY: Long,
        nextPieceType: TetrominoType,
        nextPieceRotation: Long,
        nextQueue: String = "",
        holdPieceType: TetrominoType? = null,
        holdPieceRotation: Long = 0,
        canHold: Boolean = true,
        isGameOver: Boolean,
        isPaused: Boolean,
        boardWidth: Long,
        boardHeight: Long,
        boardCells: List<BoardCells>,
    ) = withContext(dispatchers.io) {
        databaseManager.getDb().transaction {
            // Save game state
            databaseManager.getDb().currentGameStateQueries.insertOrReplaceGameState(
                score = score,
                linesCleared = linesCleared,
                level = level,
                piecesPlaced = piecesPlaced,
                maxCombo = maxCombo,
                tetrisesCleared = tetrisesCleared,
                tSpinClears = tSpinClears,
                perfectClears = perfectClears,
                hardDrops = hardDrops,
                hardDropCells = hardDropCells,
                softDropCells = softDropCells,
                backToBackChain = backToBackChain,
                isTSpinEligible = isTSpinEligible,
                currentPieceType = currentPieceType,
                currentPieceRotation = currentPieceRotation,
                currentPositionX = currentPositionX,
                currentPositionY = currentPositionY,
                nextPieceType = nextPieceType,
                nextPieceRotation = nextPieceRotation,
                nextQueue = nextQueue,
                holdPieceType = holdPieceType,
                holdPieceRotation = holdPieceRotation,
                canHold = canHold,
                isGameOver = isGameOver,
                isPaused = isPaused,
                boardWidth = boardWidth,
                boardHeight = boardHeight,
            )

            // Clear old board cells
            databaseManager.getDb().boardCellsQueries.clearAllCells()

            // Insert new board cells
            boardCells.forEach { cell ->
                databaseManager.getDb().boardCellsQueries.insertCell(
                    positionX = cell.positionX,
                    positionY = cell.positionY,
                    pieceType = cell.pieceType,
                )
            }
        }
    }

    suspend fun getGameState(): CurrentGameState? =
        withContext(dispatchers.io) {
            databaseManager
                .getDb()
                .currentGameStateQueries
                .getGameState()
                .awaitAsOneOrNull()
        }

    suspend fun getBoardCells(): List<BoardCells> =
        withContext(dispatchers.io) {
            databaseManager
                .getDb()
                .boardCellsQueries
                .getAllCells()
                .awaitAsList()
        }

    suspend fun clearGameState() =
        withContext(dispatchers.io) {
            databaseManager.getDb().transaction {
                databaseManager.getDb().currentGameStateQueries.clearGameState()
                databaseManager.getDb().boardCellsQueries.clearAllCells()
            }
        }

    suspend fun hasSavedState(): Boolean =
        withContext(dispatchers.io) {
            databaseManager
                .getDb()
                .currentGameStateQueries
                .hasSavedState()
                .awaitAsOne()
        }

    fun observeGameState(): Flow<CurrentGameState?> =
        flow {
            val db = databaseManager.getDb()
            emitAll(
                db.currentGameStateQueries
                    .getGameState()
                    .asFlow()
                    .mapToOneOrNull(dispatchers.io),
            )
        }
}
